import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import SmartReport from "../../Components/Accounting/SmartReport";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import toast, { Toaster } from "react-hot-toast";

const REPORTS = [
  { key: "income", label: "Income Statement", icon: "fa-file-invoice-dollar", fn: "periodIncomeStatement" },
  { key: "balance", label: "Balance Sheet", icon: "fa-scale-balanced", fn: "periodBalanceSheet" },
  { key: "trial", label: "Trial Balance", icon: "fa-table-list", fn: "periodTrialBalance" },
  { key: "cash", label: "Cash Flow", icon: "fa-money-bill-transfer", fn: "periodCashFlow" },
  { key: "quarterly", label: "Quarterly Income", icon: "fa-calendar-week", fn: "periodQuarterlyIncome" },
  { key: "monthly", label: "Monthly Income", icon: "fa-calendar-day", fn: "periodMonthlyIncome" },
  { key: "yoy", label: "Year over Year", icon: "fa-arrow-trend-up", fn: "periodYearOverYear" },
];

const PeriodReports = () => {
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [compareFY, setCompareFY] = useState("");
  const [report, setReport] = useState("income");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchFY(); }, []);
  useEffect(() => { if (selectedFY) load(); /* eslint-disable-next-line */ }, [selectedFY, report, compareFY]);

  const fetchFY = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1 || y.is_current === "1");
      setSelectedFY(String(cur ? cur.id : res.details?.[0]?.id || ""));
    }
  };

  const load = async () => {
    const r = REPORTS.find((x) => x.key === report);
    if (!r) return;
    if (report === "yoy" && !compareFY) { setResult(null); return; }
    setLoading(true); setResult(null);
    const payload = { financial_year_id: parseInt(selectedFY) };
    if (report === "yoy") payload.compare_year_id = parseInt(compareFY);
    const res = await ajaxAccounting[r.fn](payload);
    if (res.status === "OK") setResult(res.details);
    else { setResult(null); if (res.message) toast.error(res.message); }
    setLoading(false);
  };

  const fyOptions = financialYears.map((y) => ({ value: String(y.id), label: y.name + (y.is_current ? " (Current)" : "") }));

  return (
    <AppContainer
      title="Period Reports"
      previousPageTitle="Accounting"
      description="Financial statements for a chosen financial year and period — income statement, balance sheet, trial balance, cash flow, plus quarterly, monthly and year-over-year comparatives."
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select Financial Year" />
          </div>
          {report === "yoy" && (
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Compare With</label>
              <Select2 options={fyOptions} value={compareFY} onChange={setCompareFY} placeholder="Select comparison year" />
            </div>
          )}
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {REPORTS.map((r) => (
          <button key={r.key} className={`btn btn-sm ${report === r.key ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setReport(r.key)}>
            <i className={`fa-solid ${r.icon} me-1`}></i>{r.label}
          </button>
        ))}
      </div>

      <div className="card custom-card p-3">
        <h6 className="fw-semibold mb-3">{REPORTS.find((r) => r.key === report)?.label}</h6>
        {loading ? (
          <div className="text-center py-5 tx-muted">Loading…</div>
        ) : report === "yoy" && !compareFY ? (
          <div className="text-center py-5 tx-muted">Select a comparison year.</div>
        ) : result ? (
          <SmartReport data={result} />
        ) : (
          <div className="text-center py-5 tx-muted">No data for this period. Post journal entries to populate reports.</div>
        )}
      </div>
    </AppContainer>
  );
};

export default PeriodReports;
